import React, { useEffect, useMemo } from "react";
import PopupMask from "@/componentsUI/PopupMask";
import MarketingImagePopup from "@/features/common/marketingPopup";
import { openInstallLinkWithApkTransfer } from "@/features/install/apkTransfer";
import { INSTALL_TRACK_SOURCES } from "@/features/analytics/em/events";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import { getCurrentInstallLink } from "@/utils/countryConfigUtils";
import { getInstallAfterAuthPromptBannerImage } from "@/features/install/installAfterAuthPrompt/bannerImage";
import type { PackageGateDecision } from "./statusClient";
import {
  trackOnlinePackageRetirementDownloadClick,
  trackOnlinePackageRetirementView,
} from "./analytics";

type Props = Pick<
  PackageGateDecision,
  "sourceKey" | "revision" | "decisionSource" | "reason"
>;

export default function OnlinePackageRetirementGate({
  sourceKey,
  revision,
  decisionSource,
  reason,
}: Props) {
  const { t } = useTranslationPlus("install");
  const installLink = useMemo(() => getCurrentInstallLink(), []);
  const imageSrc = getInstallAfterAuthPromptBannerImage();
  const analyticsContext = useMemo(
    () => ({
      sourceKey,
      revision,
      decisionSource,
      reason,
      installLinkAvailable: Boolean(installLink),
    }),
    [decisionSource, installLink, reason, revision, sourceKey],
  );

  useEffect(() => {
    document.getElementById("start-loading")?.remove();
    const preventEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const keepGateInHistory = () => {
      window.history.pushState(
        { onlinePackageRetirementGate: true },
        "",
        window.location.href,
      );
    };
    keepGateInHistory();
    window.addEventListener("keydown", preventEscape, true);
    window.addEventListener("popstate", keepGateInHistory);
    trackOnlinePackageRetirementView(analyticsContext);

    return () => {
      window.removeEventListener("keydown", preventEscape, true);
      window.removeEventListener("popstate", keepGateInHistory);
    };
  }, [analyticsContext]);

  const handleDownload = () => {
    trackOnlinePackageRetirementDownloadClick(analyticsContext);
    if (installLink) {
      openInstallLinkWithApkTransfer(installLink, {
        source: INSTALL_TRACK_SOURCES.ONLINE_PACKAGE_RETIREMENT_GATE,
      });
    }
  };

  return (
    <PopupMask visible>
      <MarketingImagePopup
        imageSrc={imageSrc}
        imageAlt={t("download")}
        onImageClick={handleDownload}
        showCloseButton={false}
      />
    </PopupMask>
  );
}
